/* eslint-disable max-lines */
import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, type MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AzureAuthService, ConfirmDialogComponent, type ConfirmDialogData } from '@dtis/angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { concatMap, EMPTY, finalize, from, of, take, tap } from 'rxjs';
import type { BusinessUnitManagementDetailModel, OrgaItemBaseModel, UserModel } from '../../../models';
import { ManagementService } from '../../../services/management.service';
import { canManageMembership, hasCompleteMembership, membershipRemovalBlock, sameUsername } from '../business-unit-membership-policy';
import { CreateOrgItemDialogComponent } from '../dialogs/create-org-item-dialog/create-org-item-dialog.component';
import { EditBusinessUnitDialogComponent } from '../dialogs/edit-business-unit-dialog/edit-business-unit-dialog.component';
import { UserSearchDialogComponent } from '../dialogs/user-search-dialog/user-search-dialog.component';
import { BusinessUnitGeneralTabComponent } from '../tabs/business-unit-general-tab/business-unit-general-tab.component';
import { BusinessUnitOrganizationalStructureTabComponent } from '../tabs/business-unit-organizational-structure-tab/business-unit-organizational-structure-tab.component';
import { BusinessUnitUsersTabComponent } from '../tabs/business-unit-users-tab/business-unit-users-tab.component';

/**
 * BusinessUnitDetailViewComponent
 *
 * Detailed view of a business unit with tabbed sections for Overview, Users, and Administrators.
 * Supports view/edit modes with explicit state transitions.
 */
@Component({
  selector: 'dtis-admin-business-unit-detail-view',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTabsModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslocoModule,
    BusinessUnitGeneralTabComponent,
    BusinessUnitUsersTabComponent,
    BusinessUnitOrganizationalStructureTabComponent,
  ],
  templateUrl: './business-unit-detail-view.component.html',
  host: { class: 'block h-full overflow-hidden' },
  styles: [
    `
    @reference "tailwindcss";
    mat-tab-group {
      @apply flex flex-col;
      ::ng-deep {
        .mat-mdc-tab-body-wrapper {
          @apply flex-1 overflow-auto;
        }
      }
    }
  `,
  ],
})
export class BusinessUnitDetailViewComponent {
  private readonly managementService = inject(ManagementService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly azureAuth = inject(AzureAuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  businessUnitId = input<string>();
  update = output<void>();
  delete = output<string>();
  leave = output<void>();
  selectedTabIndex = signal(0);
  membershipChanging = signal(false);

  buRes = rxResource({
    params: this.businessUnitId,
    stream: ({ params }) => this.managementService.getBusinessUnit(params),
  });

  rootItem = rxResource({
    params: this.businessUnitId,
    stream: ({ params }) => (params ? this.managementService.getOrganizationStructure(params) : of(null)),
  });

  usersByRole = computed(() => {
    const users = this.membershipUsers() ?? [];
    return this.groupUsersByRole(users);
  });

  // biome-ignore lint/complexity/useLiteralKeys: This is a common pattern for grouping items by a property, and the complexity is justified.
  userCount = computed(() => this.usersByRole()['User']?.length ?? 0);

  // biome-ignore lint/complexity/useLiteralKeys: This is a common pattern for grouping items by a property, and the complexity is justified.
  adminCount = computed(() => this.usersByRole()['Administrator']?.length ?? 0);

  currentUsername(): string {
    return this.azureAuth.handleAccount()?.username ?? '';
  }

  membershipUsers = computed(() => {
    const users = this.buRes.isLoading() || this.buRes.error() ? undefined : this.buRes.value()?.users;
    return hasCompleteMembership(users) ? users : undefined;
  });

  isAdmin(): boolean {
    return canManageMembership(this.membershipUsers(), this.currentUsername());
  }

  constructor() {
    // Restore tab from query params on initial load
    // biome-ignore lint/complexity/useLiteralKeys: This is a common pattern for extracting query parameters, and the complexity is justified.
    const tabParam = this.route.snapshot.queryParams['tab'];
    if (tabParam !== undefined) {
      const tabIndex = Number.parseInt(tabParam, 10);
      if (!Number.isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        this.selectedTabIndex.set(tabIndex);
      }
    }
  }
  /**
   * Count nodes by type in the tree
   */
  getCountByType(type: string): number {
    if (!this.rootItem.hasValue()) {
      return 0;
    }

    const root = this.rootItem.value();
    if (!root) {
      return 0;
    }

    let count = 0;

    const traverse = (node: OrgaItemBaseModel) => {
      if (node.type === type) {
        count = count + 1;
      }
      if (node.children) {
        node.children.forEach((child) => {
          traverse(child);
        });
      }
    };

    traverse(root);
    return count;
  }
  /**
   * Groups users by their primary role
   */
  private groupUsersByRole(users: UserModel[]): Record<string, UserModel[]> {
    const grouped: Record<string, UserModel[]> = {
      User: [],
      Administrator: [],
    };

    users.forEach((user) => {
      if (user.roles?.includes('Administrator')) {
        // biome-ignore lint/complexity/useLiteralKeys: This is a common pattern for grouping items by a property, and the complexity is justified.
        grouped['Administrator'].push(user);
      } else {
        // biome-ignore lint/complexity/useLiteralKeys: This is a common pattern for grouping items by a property, and the complexity is justified.
        grouped['User'].push(user);
      }
    });

    return grouped;
  }

  /**
   * Handles tab change and updates query params
   */
  onTabChange(index: number): void {
    this.selectedTabIndex.set(index);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: index },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Opens the edit business unit dialog
   */
  openEditBusinessUnitDialog(): void {
    const bu = this.buRes.value();
    if (!bu) {
      return;
    }

    const dialogRef = this.dialog.open(EditBusinessUnitDialogComponent, {
      width: '500px',
      data: {
        identifier: bu.identifier,
        name: bu.name,
        description: bu.description ?? '',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.managementService
        .updateBusinessUnit({
          id: bu.identifier,
          name: result.name,
          description: result.description,
        })
        .subscribe({
          next: () => {
            this.buRes.reload();
            this.update.emit();
            this.snackBar.open('Business unit updated successfully', undefined, {
              duration: 2000,
            });
          },
          error: (err) => {
            console.error('Error updating business unit:', err);
            this.snackBar.open('Error updating business unit', undefined, {
              duration: 3000,
            });
          },
        });
    });
  }

  /**
   * Opens user search dialog
   */
  openUserSearchDialog(role: 'User' | 'Administrator') {
    const bu = this.buRes.error() ? undefined : this.buRes.value();
    if (!bu?.identifier || !this.isAdmin() || this.membershipChanging()) {
      this.membershipNotice('membership-admin-required');
      return;
    }
    const identifier = bu.identifier;
    const actor = this.currentUsername();
    this.membershipChanging.set(true);
    const dialogRef = this.dialog.open<UserSearchDialogComponent, unknown, string[]>(UserSearchDialogComponent, {
      width: '600px',
      data: {
        selectedUsers: bu.users,
        role,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        concatMap((names) => from(names ?? [])),
        concatMap((name) =>
          this.managementService.getBusinessUnit(identifier).pipe(
            take(1),
            concatMap((fresh) => {
              this.assertMembershipContext(fresh, identifier);
              const username = this.azureAuth.handleAccount()?.username ?? '';
              if (!sameUsername(actor, username) || !canManageMembership(fresh.users, username)) {
                throw new Error(this.transloco.translate('membership-admin-required'));
              }
              if (typeof name !== 'string' || !name.trim()) {
                throw new Error(this.transloco.translate('membership-data-unavailable'));
              }
              const existing = fresh.users.find((member) => sameUsername(member.name, name));
              const roles = [...new Set([...(existing?.roles ?? []), role])];
              return this.managementService.assignRoleToUser(existing?.name ?? name, identifier, roles);
            }),
            tap(() => {
              this.update.emit();
              this.membershipNotice('membership-role-added');
            })
          )
        ),
        finalize(() => {
          this.membershipChanging.set(false);
          this.buRes.reload();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (error: unknown) => this.membershipFailure(error),
      });
  }

  /**
   * Removes a user from the business unit
   */
  removeUser(user: UserModel) {
    const bu = this.buRes.error() ? undefined : this.buRes.value();
    const block = this.membershipChanging() ? 'membership-change-in-progress' : membershipRemovalBlock(this.membershipUsers(), this.currentUsername(), user);
    if (!bu?.identifier || block) {
      this.membershipNotice(block ?? 'membership-data-unavailable');
      return;
    }
    const identifier = bu.identifier;
    const actor = this.currentUsername();
    const leaving = sameUsername(user.name, actor);
    this.membershipChanging.set(true);
    const dialogRef: MatDialogRef<ConfirmDialogComponent, boolean> = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.transloco.translate(leaving ? 'membership-leave' : 'membership-remove-title'),
        message: this.transloco.translate(leaving ? 'membership-leave-confirm' : 'membership-remove-confirm', {
          user: user.name,
          businessUnit: bu.name,
        }),
      } satisfies ConfirmDialogData,
    });
    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        concatMap((confirmed) => (confirmed === true ? this.managementService.getBusinessUnit(identifier).pipe(take(1)) : EMPTY)),
        concatMap((fresh) => {
          this.assertMembershipContext(fresh, identifier);
          const username = this.azureAuth.handleAccount()?.username ?? '';
          const reason = sameUsername(username, actor) ? membershipRemovalBlock(fresh.users, username, user) : 'membership-data-unavailable';
          if (reason) {
            throw new Error(this.transloco.translate(reason));
          }
          const member = fresh.users.find((entry) => sameUsername(entry.name, user.name));
          if (!member) {
            throw new Error(this.transloco.translate('membership-data-unavailable'));
          }
          return this.managementService.assignRoleToUser(member.name, identifier, []);
        }),
        finalize(() => this.membershipChanging.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.membershipNotice(leaving ? 'membership-left' : 'membership-removed');
          if (leaving && this.businessUnitId() === identifier) {
            this.leave.emit();
          } else {
            this.buRes.reload();
            this.update.emit();
          }
        },
        error: (error: unknown) => {
          this.buRes.reload();
          this.membershipFailure(error);
        },
      });
  }

  private assertMembershipContext(bu: BusinessUnitManagementDetailModel, identifier: string): void {
    if (this.businessUnitId() !== identifier || bu.identifier !== identifier) {
      throw new Error(this.transloco.translate('membership-data-unavailable'));
    }
  }

  private membershipNotice(key: string): void {
    this.snackBar.open(this.transloco.translate(key), undefined, { duration: 4000 });
  }

  private membershipFailure(error: unknown): void {
    console.error('Business unit membership change failed:', error);
    const message = error instanceof Error ? error.message : '';
    const failureMessage = this.transloco.translate('membership-change-failed');
    this.snackBar.open(failureMessage + (message ? `: ${message}` : ''), undefined, { duration: 5000 });
  }

  /**
   * Handle create child item action
   */
  createChildItem(parentNode: OrgaItemBaseModel): void {
    // Determine child type based on parent type
    const childTypeMap: Record<string, string> = {
      UseCaseRoot: 'Master',
      Master: 'Instance',
      Instance: 'Configuration',
    };

    // Support both flat `type` and nested `elementType.type` from API
    const elementTypeNode = parentNode as OrgaItemBaseModel & { elementType?: { type?: string } };
    const nodeType = parentNode.type ?? elementTypeNode.elementType?.type ?? '';
    const childType = childTypeMap[nodeType];
    if (!childType) {
      this.snackBar.open('Cannot add children to this node type', undefined, {
        duration: 3000,
      });
      return;
    }

    const dialogRef = this.dialog.open(CreateOrgItemDialogComponent, {
      width: '500px',
      data: {
        parentNode,
        childType,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !parentNode.identifier) {
        return;
      }

      // Create the new organizational item
      this.managementService
        .createOrganizationStructure({
          parentId: parentNode.identifier,
          items: [
            {
              name: result.name,
              description: result.description || '',
              type: childType,
            },
          ],
        })
        .subscribe({
          next: () => {
            this.rootItem.reload();
            this.snackBar.open(`${childType} created successfully`, undefined, {
              duration: 2000,
            });
          },
          error: (err) => {
            console.error('Error creating organizational item:', err);
            this.snackBar.open(`Error creating ${childType}`, undefined, {
              duration: 3000,
            });
          },
        });
    });
  }

  /**
   * Handle delete organizational item action
   */
  deleteOrganizationItem() {
    alert('not implemented yet');
  }
}
